import { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, DollarSign, Loader2, User, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/app/hooks';
import { useToast } from '@/hooks/use-toast';
import { analytics } from '@/services/analytics';
import { Checkbox } from '@/components/ui/checkbox';
import { createAdminOrderRefund, fetchAdminOrder, type AdminOrderRefundDetails } from '@/services/adminService';
import { logging } from '@/services/logging';

const REFUND_REASONS = [
  { value: 'refund shipment cost', label: 'Refund shipment cost' },
  { value: 'refund whole order', label: 'Refund whole order' },
  { value: 'refund due custom reason', label: 'Refund due custom reason' },
] as const;

type RefundReason = typeof REFUND_REASONS[number]['value'];

const AdminOrderRefundPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';

  const orderId = searchParams.get('orderId');

  const [selectedReason, setSelectedReason] = useState<RefundReason | ''>('');
  const [customReason, setCustomReason] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [order, setOrder] = useState<AdminOrderRefundDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [informUser, setInformUser] = useState(true);

  useEffect(() => {
    if (orderId) {
      analytics.track('page_view', { page: 'admin_order_refund', orderId });
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setIsLoading(true);
    try {
      const orderData = await fetchAdminOrder(orderId);
      setOrder(orderData);
      // Default to refund shipment cost with shipping amount
      setSelectedReason('refund shipment cost');
      if (orderData.shippingAmountUsd) {
        setAmount(orderData.shippingAmountUsd.toFixed(2));
      }
    } catch (err) {
      logging.error('Failed to load order for refund', { error: err, orderId });
      toast({
        title: 'Error loading order',
        description: 'Failed to load order details.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [orderId, toast]);

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only positive numbers with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  }, []);

  const handleReasonChange = useCallback((value: RefundReason) => {
    setSelectedReason(value);
    // Auto-fill amount based on reason
    if (value === 'refund shipment cost' && order?.shippingAmountUsd) {
      setAmount(order.shippingAmountUsd.toFixed(2));
    } else if (value === 'refund whole order' && order?.orderAmountUsd) {
      setAmount(order.orderAmountUsd.toFixed(2));
    } else {
      setAmount('');
    }
  }, [order]);

  const handleSubmit = useCallback(async () => {
    if (!orderId) {
      toast({
        title: 'Error',
        description: 'Order ID is required.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedReason) {
      toast({
        title: 'Error',
        description: 'Please select a refund reason.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedReason === 'refund due custom reason' && !customReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a custom reason.',
        variant: 'destructive',
      });
      return;
    }

    const amountNum = Number.parseFloat(amount);
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid refund amount greater than 0.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    analytics.track('button_click', { action: 'admin_refund_submit', orderId, reason: selectedReason, informUser });

    try {
      const result = await createAdminOrderRefund(orderId, {
        amount: amountNum,
        reason: selectedReason,
        customReason: selectedReason === 'refund due custom reason' ? customReason.trim() : undefined,
        informUser,
      });

      toast({
        title: 'Refund processed successfully',
        description: `Refund of $${amountNum.toFixed(2)} USD recorded. Payment ID: ${result.refund.id}`,
      });

      logging.info('Admin refund processed', {
        orderId,
        refundId: result.refund.id,
        amount: amountNum,
        reason: selectedReason,
        telegramSent: result.telegramNotification.sent,
      });

      // Navigate back to orders page after short delay
      setTimeout(() => {
        navigate('/admin/orders');
      }, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process refund.';
      logging.error('Admin refund failed', { error: err, orderId, amount: amountNum });
      toast({
        title: 'Refund failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [orderId, selectedReason, customReason, amount, navigate, toast]);

  const handleCancel = useCallback(() => {
    analytics.track('button_click', { action: 'admin_refund_cancel', orderId: orderId ?? 'unknown' });
    navigate('/admin/orders');
  }, [navigate, orderId]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">Loading order details...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Admin access required</CardTitle>
            <CardDescription>You do not have permission to view this page.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // If no orderId in URL, show error and redirect option
  if (!orderId) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Missing Order ID</CardTitle>
            <CardDescription>
              This page requires an order ID to process refunds. Please navigate here from the Orders Management page.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/admin/orders')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go to Orders
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const showCustomReason = selectedReason === 'refund due custom reason';

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={handleCancel}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Orders
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Process Order Refund</CardTitle>
          <CardDescription>
            Order ID: <code className="rounded bg-muted px-1 py-0.5 text-sm">{orderId}</code>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Order Info */}
          {order && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Customer</p>
                  <p className="text-sm text-muted-foreground">
                    {order.customer?.name ?? order.customer?.email ?? 'Unknown'}
                  </p>
                </div>
              </div>
              {order.shippingAddress && (
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Shipping Address</p>
                    <p className="text-sm text-muted-foreground">
                      {order.shippingAddress.firstName} {order.shippingAddress.lastName}, {order.shippingAddress.street1}
                      {order.shippingAddress.street2 && `, ${order.shippingAddress.street2}`}
                      <br />
                      {order.shippingAddress.city}, {order.shippingAddress.region} {order.shippingAddress.postalCode}, {order.shippingAddress.countryCode}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                {order.orderAmountUsd !== null && (
                  <Badge variant="secondary">Order: ${order.orderAmountUsd.toFixed(2)}</Badge>
                )}
                {order.shippingAmountUsd !== null && (
                  <Badge variant="outline">Shipping: ${order.shippingAmountUsd.toFixed(2)}</Badge>
                )}
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
              <div className="space-y-1">
                <p className="font-medium text-red-800">
                  УВАГА! Цей інтерфейс змінює баланс користувача. Будь ласка, виконуйте дії тільки після схвалення.
                </p>
                <p className="text-sm text-red-700">
                  WARNING! This interface changes the user balance. Please perform actions only after approval.
                </p>
              </div>
            </div>
          </div>

          {/* Refund Reason Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="refund-reason">Refund Reason</Label>
            <Select
              value={selectedReason}
              onValueChange={handleReasonChange}
            >
              <SelectTrigger id="refund-reason">
                <SelectValue placeholder="Select a refund reason" />
              </SelectTrigger>
              <SelectContent>
                {REFUND_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Reason Field - shown only when custom reason selected */}
          {showCustomReason && (
            <div className="space-y-2">
              <Label htmlFor="custom-reason">Custom Reason</Label>
              <Input
                id="custom-reason"
                placeholder="Enter custom refund reason"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {customReason.trim().length < 5 ? (
                  <span className="text-red-600">Minimum 5 characters required ({customReason.trim().length}/5)</span>
                ) : (
                  'Reason is valid'
                )}
              </p>
            </div>
          )}

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="refund-amount">Refund Amount (USD)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="refund-amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={handleAmountChange}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Enter the refund amount in US Dollars (USD).
            </p>
          </div>

          {/* Inform User Checkbox */}
          <div className="flex items-start space-x-3 rounded-lg border p-4">
            <Checkbox
              id="inform-user"
              checked={informUser}
              onCheckedChange={(checked: boolean | 'indeterminate') => {
                const newValue = checked === true;
                setInformUser(newValue);
                analytics.track('ui.refund_inform_user_toggle', { orderId, informUser: newValue });
              }}
            />
            <div className="space-y-1 leading-none">
              <Label
                htmlFor="inform-user"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Inform user
              </Label>
              <p className="text-xs text-muted-foreground">
                Send a notification to the user about this refund.
              </p>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !selectedReason ||
              !amount ||
              Number.parseFloat(amount) <= 0 ||
              (showCustomReason && customReason.trim().length < 5) ||
              isSubmitting
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Process Refund'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default AdminOrderRefundPage;
